/**
 * Kenyan phone numbers, normalised to E.164.
 *
 * Dispatchers will type `0712345678`, `+254 712 345 678`, `254712345678` and
 * `0112 345 678` — all of which are the same person. Accept every form, store
 * exactly one (NOTIFICATIONS.md §5).
 */
const KE_COUNTRY_CODE = '254';
/** Kenyan mobile prefixes after the country code: 7xx (Safaricom/Airtel) and 1xx. */
const KE_MOBILE = /^254(7\d{8}|1\d{8})$/;
export class InvalidPhoneNumberError extends Error {
    constructor(input) {
        super(`"${input}" is not a valid Kenyan mobile number`);
        this.name = 'InvalidPhoneNumberError';
    }
}
/**
 * @throws InvalidPhoneNumberError when the input cannot be a Kenyan mobile.
 */
export function toE164(input) {
    const digits = input.replace(/[^\d+]/g, '');
    let national;
    if (digits.startsWith('+254')) {
        national = digits.slice(1);
    }
    else if (digits.startsWith('254')) {
        national = digits;
    }
    else if (digits.startsWith('0')) {
        national = KE_COUNTRY_CODE + digits.slice(1);
    }
    else if (digits.startsWith('+')) {
        // Another country — pass through, we only validate Kenyan numbers.
        const stripped = digits.slice(1);
        if (!/^\d{8,15}$/.test(stripped))
            throw new InvalidPhoneNumberError(input);
        return `+${stripped}`;
    }
    else {
        national = KE_COUNTRY_CODE + digits;
    }
    if (!KE_MOBILE.test(national))
        throw new InvalidPhoneNumberError(input);
    return `+${national}`;
}
/**
 * What is wrong with a stored number, in words somebody can act on.
 *
 * ## Why a reason rather than a boolean
 *
 * Because "invalid" covers two situations that need opposite responses, and
 * conflating them wastes somebody's afternoon.
 *
 * A number that cannot be a Kenyan mobile at all has to be corrected — there
 * is no message going anywhere until somebody asks the landlord what their
 * number is. A number that is merely written as `0798…` instead of `+254798…`
 * is perfectly usable: everything that sends normalises it on the way out. It
 * is untidy, not broken, and telling somebody it is broken sends them chasing
 * a landlord who was never unreachable.
 *
 * Null means nothing to say.
 */
export function phoneProblem(raw) {
    if (!raw || !raw.trim())
        return 'No number on file, so nothing can be sent.';
    let normalised;
    try {
        normalised = toE164(raw);
    }
    catch {
        return 'Not a Kenyan mobile number. Anything sent to it will fail.';
    }
    /*
     * Stored in a form other than the one everything else uses. Worth saying,
     * because two records for one landlord — `0798…` and `+254798…` — look like
     * two different people in every list that sorts or groups by it.
     */
    if (normalised !== raw.trim()) {
        return `Stored as "${raw.trim()}". It works, but ${normalised} is the form everything else uses.`;
    }
    return null;
}
/** True only for a number that cannot be used at all. */
export function phoneIsUnusable(raw) {
    if (!raw || !raw.trim())
        return true;
    try {
        toE164(raw);
        return false;
    }
    catch {
        return true;
    }
}
export function isValidE164(value) {
    return /^\+\d{8,15}$/.test(value);
}
/**
 * `+254712345678` → `+254 712 345 678`. For display on a ticket, where someone
 * is about to dial it.
 */
export function formatE164(value) {
    const m = /^\+254(\d{3})(\d{3})(\d{3})$/.exec(value);
    return m ? `+254 ${m[1]} ${m[2]} ${m[3]}` : value;
}
/**
 * `+254712345678` → `+254 7XX XXX 678`. For lists and logs — OPERATIONS.md §4
 * says never log a full number.
 */
export function maskPhone(value) {
    const m = /^\+254(\d)(\d{2})(\d{3})(\d{3})$/.exec(value);
    return m ? `+254 ${m[1]}XX XXX ${m[4]}` : '***';
}
//# sourceMappingURL=phone.js.map